import { useTranslation } from 'react-i18next';
import { Button, LenderChip, Price } from '@hero/web-components';
import { useFinancingPartnersListApi } from '@hero/api-hooks';
import { TERM_DURATION_OPTIONS } from '../../mocks';
import type { ApplyFormValues } from './types';
import type { WizardStepKey } from './WizardStepper';

export type ReviewStepProps = {
  values: ApplyFormValues;
  onEdit: (step: WizardStepKey) => void;
};

const termLabelFromKey = (key: string): string => {
  const found = TERM_DURATION_OPTIONS.find(opt => {
    if (opt.unit === 'days') return `${opt.value}_days` === key;
    if (opt.unit === 'months') return key === '6_months';
    return key === '1_year';
  });
  return found?.label ?? key;
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
    <span className="text-end text-sm font-medium text-gray-900">{value}</span>
  </div>
);

const Section = ({
  title,
  step,
  onEdit,
  children,
}: {
  title: string;
  /** Omit `step`/`onEdit` to render a read-only section (no Edit button). */
  step?: WizardStepKey;
  onEdit?: (step: WizardStepKey) => void;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation('m-financing');
  const canEdit = Boolean(step && onEdit);
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <header className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {canEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => step && onEdit?.(step)}
            className="-me-2 text-emerald-700 hover:bg-emerald-50">
            {t('apply_form.review_summary.edit')}
          </Button>
        ) : null}
      </header>
      <div className="divide-y divide-gray-100">{children}</div>
    </section>
  );
};

/**
 * Compact review-before-submit panel. Shows a digestible summary
 * of every step so the carrier can spot a wrong number before
 * sending. Every section has an inline Edit jump-back link to the
 * step that owns those fields.
 */
export function ReviewStep({ values, onEdit }: ReviewStepProps) {
  const { t } = useTranslation('m-financing');

  /**
   * Lender targeting is handled server-side — the application is
   * fanned out to every active pilot partner. The review section
   * just shows that full list so the carrier knows who'll see it.
   * Partner copy is already localized by the backend.
   */
  const partnersQuery = useFinancingPartnersListApi({});
  const targetedLenders = partnersQuery.data?.partners ?? [];

  const invoiceAmount =
    values.invoiceTab === 'external' ? Number(values.externalAmount || 0) : 0;
  const invoiceCustomer =
    values.invoiceTab === 'external' ? values.externalCustomerName : '—';
  const invoiceDue =
    values.invoiceTab === 'external' && values.externalExpectedDueDate
      ? values.externalExpectedDueDate.toLocaleDateString()
      : '—';

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          {t('apply_form.review_summary.title')}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {t('apply_form.review_summary.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Section
          title={t('apply_form.review_summary.invoice_section')}
          step="invoice"
          onEdit={onEdit}>
          <Row
            label={t('invoice_source.hero')}
            value={t(`invoice_source.${values.invoiceTab}`)}
          />
          {values.invoiceTab === 'external' ? (
            <>
              <Row
                label={t('apply_form.invoice.external.client_name')}
                value={invoiceCustomer || '—'}
              />
              <Row
                label={t('apply_form.invoice.external.amount')}
                value={
                  <Price
                    amount={invoiceAmount}
                    currency="SAR"
                    className="font-semibold text-emerald-700"
                  />
                }
              />
              <Row
                label={t('apply_form.invoice.external.expected_due_date')}
                value={invoiceDue}
              />
            </>
          ) : (
            <>
              <Row
                label={t('apply_form.invoice.external.client_name')}
                value={values.heroInvoiceCustomerName || '—'}
              />
              <Row
                label={t('apply_form.invoice.external.amount')}
                value={
                  <Price
                    amount={Number(values.heroInvoiceAmount) || 0}
                    currency="SAR"
                    className="font-semibold text-emerald-700"
                  />
                }
              />
              {values.heroExpectedDueDate && (
                <Row
                  label={t('apply_form.invoice.external.expected_due_date')}
                  value={values.heroExpectedDueDate.toLocaleDateString()}
                />
              )}
              {values.heroInvoiceNumber && (
                <Row
                  label={t('apply_form.invoice.external.invoice_number')}
                  value={`#${values.heroInvoiceNumber}`}
                />
              )}
            </>
          )}
        </Section>

        <Section
          title={t('apply_form.review_summary.terms_section')}
          step="terms"
          onEdit={onEdit}>
          <Row
            label={t('apply_form.terms.requested_amount.title')}
            value={
              <Price
                amount={Number(values.requestedAmount || 0)}
                currency="SAR"
                className="font-semibold text-emerald-700"
              />
            }
          />
          <Row
            label={t('apply_form.terms.requested_term.title')}
            value={termLabelFromKey(values.requestedTermKey)}
          />
        </Section>

        <Section title={t('apply_form.review_summary.lenders_section')}>
          <div className="space-y-2 py-1">
            <p className="text-xs text-gray-500">
              {t('apply_form.review_summary.lenders_note')}
            </p>
            <div className="flex flex-wrap gap-2">
              {targetedLenders.map(lender => (
                <LenderChip key={lender.id} lender={lender} size="sm" />
              ))}
            </div>
          </div>
        </Section>

        <Section
          title={t('apply_form.review_summary.profile_section')}
          step="terms"
          onEdit={onEdit}>
          <Row
            label={t('apply_form.profile.fields.fleet_tier.title')}
            value={
              values.fleetTier ? t(`fleet_tier.${values.fleetTier}`) : '—'
            }
          />
          <Row
            label={t('apply_form.profile.fields.annual_revenue_tier.title')}
            value={
              values.annualRevenueTier
                ? t(`revenue_tier.${values.annualRevenueTier}`)
                : '—'
            }
          />
          {values.isProfitable !== '' && (
            <Row
              label={t('apply_form.profile.financials.is_profitable.title')}
              value={t(
                values.isProfitable === 'yes'
                  ? 'apply_form.profile.financials.is_profitable.yes'
                  : 'apply_form.profile.financials.is_profitable.no',
              )}
            />
          )}
        </Section>
      </div>
    </div>
  );
}
